import { useTranslation } from 'react-i18next'

import type { AiFamilyChoice, AiProvider, AiProvidersResponse } from '../../api/schema'
import { Badge } from '../ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'

/** Select value standing for "the provider's default model" (Radix items can't be ''). */
export const DEFAULT_MODEL = 'default'
/** Select value standing for "no provider of its own" (Vision follows Text, Transcription the stack). */
export const NO_PROVIDER = 'inherit'

export type SelectableFamily = 'extraction' | 'vision' | 'transcription'
export type FamilyChange = { family: SelectableFamily; provider: string; model: string }

const SELECTABLE: SelectableFamily[] = ['extraction', 'vision', 'transcription']

/** The first family keeps the testids the screen has always had; the others are prefixed. */
const tid = (family: string, name: string) => (family === 'extraction' ? `ai-models-${name}` : `ai-models-${family}-${name}`)

/** Providers a family can pick. Anthropic serves agents only; vision needs a model that reads images. */
export function providersFor(family: SelectableFamily, all: AiProvider[]): AiProvider[] {
    const usable = all.filter((p) => p.type !== 'anthropic')
    if (family === 'transcription') return usable.filter((p) => p.type === 'gemini' || p.type === 'openai_compatible')
    if (family === 'vision') return usable.filter((p) => p.type === 'gemini' || (p.vision_models?.length ?? 0) > 0)
    return usable
}

/** Models a family can pick on a provider: the vision subset for Vision on a non-Gemini provider. */
export function modelsFor(family: SelectableFamily, p: AiProvider | undefined): string[] {
    if (!p) return []
    if (family === 'vision' && p.type !== 'gemini') return p.vision_models ?? []
    return p.models
}

const isGoogle = (provider: string, all: AiProvider[]) => all.find((p) => p.name === provider)?.type === 'gemini'

function HostingCell({ family, hosting, google }: { family: string; hosting: string; google: boolean }) {
    const { t } = useTranslation('settings')
    return (
        <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground" data-testid={tid(family, 'hosting')}>
            {google && (
                <Badge variant="info" data-testid={tid(family, 'google-label')}>
                    {t('ai_models.families.google')}
                </Badge>
            )}
            {hosting && !(google && hosting === 'Google') ? <span>{hosting}</span> : !google && !hosting && <span>{t('ai_models.families.hosting_unknown')}</span>}
        </div>
    )
}

function SelectableRow({
    family,
    choice,
    providers,
    canWrite,
    pending,
    onChange,
}: {
    family: SelectableFamily
    choice: AiFamilyChoice & { effective_provider?: string }
    providers: AiProvider[]
    canWrite: boolean
    pending: boolean
    onChange: (c: FamilyChange) => void
}) {
    const { t } = useTranslation('settings')
    const options = providersFor(family, providers)
    const shown = choice.provider || choice.effective_provider || ''
    const current = providers.find((p) => p.name === (choice.provider || shown))
    const models = modelsFor(family, current)
    const canInherit = family !== 'extraction'
    // An inherited family reports the provider it follows; its own choice is empty.
    const providerValue = canInherit && choice.inherited ? NO_PROVIDER : choice.provider
    // A stored model the server no longer lists reads as the default one.
    const modelValue = choice.model && models.includes(choice.model) ? choice.model : DEFAULT_MODEL
    const disabled = pending
    const inheritLabel = t(`ai_models.families.inherit_${family}`)

    return (
        <tr className="border-t border-border align-top" data-testid={`ai-models-row-${family}`}>
            <td className="py-3 pr-4">
                <div className="text-sm font-medium text-foreground">{t(`ai_models.families.${family}.name`)}</div>
                <div className="text-xs text-muted-foreground mt-1">{t(`ai_models.families.${family}.description`)}</div>
                {choice.inherited && (
                    <div className="text-xs text-muted-foreground mt-1" data-testid={tid(family, 'inherited-hint')}>
                        {t(`ai_models.families.inherited_${family}`, { provider: shown })}
                    </div>
                )}
            </td>
            <td className="py-3 pr-4 min-w-48">
                {canWrite ? (
                    <Select
                        value={providerValue}
                        onValueChange={(v) => onChange({ family, provider: v === NO_PROVIDER ? '' : v, model: '' })}
                        disabled={disabled}
                    >
                        <SelectTrigger data-testid={tid(family, 'provider-select')}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {canInherit && (
                                <SelectItem value={NO_PROVIDER} data-testid={tid(family, 'provider-option-inherit')}>
                                    {inheritLabel}
                                </SelectItem>
                            )}
                            {options.map((p) => (
                                <SelectItem key={p.name} value={p.name} data-testid={tid(family, `provider-option-${p.name}`)}>
                                    {p.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                    <div className="text-sm text-foreground" data-testid={tid(family, 'provider-value')}>
                        {shown || '-'}
                    </div>
                )}
            </td>
            <td className="py-3 pr-4 min-w-48">
                {canWrite ? (
                    <Select
                        value={modelValue}
                        onValueChange={(m) => onChange({ family, provider: choice.provider, model: m === DEFAULT_MODEL ? '' : m })}
                        disabled={disabled || (canInherit && choice.inherited)}
                    >
                        <SelectTrigger data-testid={tid(family, 'model-select')}>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value={DEFAULT_MODEL} data-testid={tid(family, 'model-option-default')}>
                                {t('ai_models.model.default', { model: models[0] ?? choice.effective_model })}
                            </SelectItem>
                            {models.map((m) => (
                                <SelectItem key={m} value={m} data-testid={tid(family, `model-option-${m}`)}>
                                    {m}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                ) : (
                    <div className="text-sm text-foreground" data-testid={tid(family, 'model-value')}>
                        {choice.effective_model || t('ai_models.model.provider_default')}
                    </div>
                )}
            </td>
            <td className="py-3">
                <HostingCell family={family} hosting={choice.hosting} google={isGoogle(shown, providers)} />
            </td>
        </tr>
    )
}

function StaticRow({ family, value, model, hosting, google }: { family: string; value: string; model?: string; hosting: string; google: boolean }) {
    const { t } = useTranslation('settings')
    return (
        <tr className="border-t border-border align-top" data-testid={`ai-models-row-${family}`}>
            <td className="py-3 pr-4">
                <div className="text-sm font-medium text-foreground">{t(`ai_models.families.${family}.name`)}</div>
                <div className="text-xs text-muted-foreground mt-1">{t(`ai_models.families.${family}.description`)}</div>
            </td>
            <td className="py-3 pr-4 text-sm text-foreground" data-testid={`ai-models-${family}-row-value`}>
                {value || '-'}
            </td>
            <td className="py-3 pr-4 text-sm text-foreground">{model ?? ''}</td>
            <td className="py-3">
                <HostingCell family={family} hosting={hosting} google={google} />
            </td>
        </tr>
    )
}

/**
 * One row per AI processing family (spec 2026-11-03-03 §3): who runs it, on
 * which model, and where that provider runs. Text, Vision and Transcription
 * are chosen here by platform admins. OCR, Embedding and Agents show their
 * current state and are edited in the sections below.
 */
export function FamilyTable({
    data,
    canWrite,
    pending,
    onChange,
}: {
    data: AiProvidersResponse
    canWrite: boolean
    pending: boolean
    onChange: (c: FamilyChange) => void
}) {
    const { t } = useTranslation('settings')
    const { providers, ocr, embedding, agent } = data
    const choices: Record<SelectableFamily, AiFamilyChoice & { effective_provider?: string }> = {
        extraction: data.extraction,
        vision: data.vision,
        transcription: data.transcription,
    }
    const agentProviders = agent.allowed_providers.join(', ')

    return (
        <section className="bg-card rounded-lg border border-border p-6" data-testid="ai-models-families">
            <h2 className="text-base font-semibold text-foreground">{t('ai_models.families.title')}</h2>
            <p className="text-muted-foreground text-sm mt-1 mb-4">{t('ai_models.families.description')}</p>
            <div className="overflow-x-auto">
                <table className="w-full text-left" data-testid="ai-models-family-table">
                    <thead>
                        <tr className="text-xs text-muted-foreground">
                            <th className="pb-2 pr-4 font-medium">{t('ai_models.families.col_family')}</th>
                            <th className="pb-2 pr-4 font-medium">{t('ai_models.provider.label')}</th>
                            <th className="pb-2 pr-4 font-medium">{t('ai_models.model.label')}</th>
                            <th className="pb-2 font-medium">{t('ai_models.families.col_hosting')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {SELECTABLE.map((f) => (
                            <SelectableRow key={f} family={f} choice={choices[f]} providers={providers} canWrite={canWrite} pending={pending} onChange={onChange} />
                        ))}
                        <StaticRow family="ocr" value={ocr.provider} hosting={ocr.hosting} google={ocr.provider === 'google'} />
                        <StaticRow family="embedding" value={embedding.active} hosting={embedding.hosting} google={false} />
                        <StaticRow family="agents" value={agentProviders || t('ai_models.families.agents.none')} hosting="" google={false} />
                    </tbody>
                </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4" data-testid="ai-models-no-recompute">
                {t('ai_models.no_recompute')}
            </p>
            {!canWrite && (
                <p className="text-xs text-muted-foreground mt-2" data-testid="ai-models-read-only">
                    {t('ai_models.read_only')}
                </p>
            )}
        </section>
    )
}
